import React, { createContext, useContext, useEffect, useState } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from './supabase';
import { Navigate } from 'react-router-dom';

interface UserWithRole {
  id: string;
  email: string;
  role: 'admin' | 'user';
}

interface AuthContextType {
  session: Session | null;
  user: User | null;
  userDetails: UserWithRole | null;
  isAdmin: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [userDetails, setUserDetails] = useState<UserWithRole | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchUserDetails(session.user);
      } else {
        setLoading(false);
      }
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchUserDetails(session.user);
      } else {
        setUserDetails(null);
        setIsAdmin(false);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const fetchUserDetails = async (authUser: User) => {
    try {
      // First, try to get the user by ID (from auth)
      const { data: userById, error: idError } = await supabase
        .from('users')
        .select('id, email, role')
        .eq('id', authUser.id)
        .maybeSingle();

      if (userById) {
        // Found user by ID
        setUserDetails(userById as UserWithRole);
        setIsAdmin(userById.role === 'admin');
        setLoading(false);
        return;
      }

      // If not found by ID, try to find by email
      if (authUser.email) {
        const { data: userByEmail, error: emailError } = await supabase
          .from('users')
          .select('id, email, role')
          .eq('email', authUser.email)
          .maybeSingle();

        if (userByEmail) {
          // User found by email but has different ID
          // Update the user record to match the auth ID
          const { error: updateError } = await supabase
            .from('users')
            .update({ id: authUser.id })
            .eq('id', userByEmail.id);

          if (updateError) {
            console.error('Error updating user ID:', updateError);
          } else {
            // Get the updated user record
            const { data: updatedUser } = await supabase
              .from('users')
              .select('id, email, role')
              .eq('id', authUser.id)
              .single();

            if (updatedUser) {
              setUserDetails(updatedUser as UserWithRole);
              setIsAdmin(updatedUser.role === 'admin');
              setLoading(false);
              return;
            }
          }
        }
      }

      // No user found by ID or email, create a new user
      if (authUser.email) {
        try {
          // Generate a unique user_id if needed
          const uniqueUserId = `user-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
          
          const { data: newUser, error: insertError } = await supabase
            .from('users')
            .insert({
              id: authUser.id,
              email: authUser.email,
              role: 'user', // Default role
              user_id: uniqueUserId // Provide a unique user_id
            })
            .select()
            .single();
            
          if (insertError) {
            console.error('Error creating user record:', insertError);
            
            // If we still have a duplicate key issue, fetch the existing user as a fallback
            if (insertError.code === '23505') { // Unique violation
              const { data: existingUser } = await supabase
                .from('users')
                .select('id, email, role')
                .eq('email', authUser.email)
                .single();
                
              if (existingUser) {
                setUserDetails(existingUser as UserWithRole);
                setIsAdmin(existingUser.role === 'admin');
              }
            }
          } else if (newUser) {
            setUserDetails(newUser as UserWithRole);
            setIsAdmin(newUser.role === 'admin');
          }
        } catch (createError) {
          console.error('Error in user creation:', createError);
        }
      }
    } catch (error) {
      console.error('Unexpected error fetching user details:', error);
      setUserDetails(null);
      setIsAdmin(false);
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
  };

  const signUp = async (email: string, password: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
    });
    if (error) throw error;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ 
      session, 
      user, 
      userDetails, 
      isAdmin, 
      signIn, 
      signUp, 
      signOut,
      loading
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function RequireAuth({ children }: { children: React.ReactNode }) {
  const { session } = useAuth();

  if (!session) {
    return <Navigate to="/login" />;
  }

  return <>{children}</>;
}

export function RequireAdmin({ children }: { children: React.ReactNode }) {
  const { isAdmin, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!isAdmin) {
    return <Navigate to="/unauthorized" />;
  }

  return <>{children}</>;
}